import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { queueAddress } from "@/lib/offlineDb";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { MapPin, Camera, ImagePlus, Loader2, X, ArrowLeft, AlertTriangle, Sparkles } from "lucide-react";

const AddressFinderAdd = () => {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [gpsBusy, setGpsBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiSummary, setAiSummary] = useState(null); // {ok, filledKeys[], reason}
  const [form, setForm] = useState({
    title: "", property_name: "", address_line_1: "", town: "", postcode: "",
    latitude: null, longitude: null, gps_accuracy: null,
    entrance_instructions: "", driver_notes: "", vehicle_size_warning: "",
    landmark: "",
    what3words: "",
    visibility: "company",
  });
  const [images, setImages] = useState([]); // {file, previewUrl, analysed}
  const [dupes, setDupes] = useState([]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const getGps = () => {
    if (!navigator.geolocation) { toast.error("GPS not supported"); return; }
    setGpsBusy(true);
    navigator.geolocation.getCurrentPosition(
      async (p) => {
        set("latitude", p.coords.latitude);
        set("longitude", p.coords.longitude);
        set("gps_accuracy", Math.round(p.coords.accuracy));
        toast.success(`GPS captured (±${Math.round(p.coords.accuracy)}m)`);
        setGpsBusy(false);
        // Reverse geocode → auto-fill postcode + town if empty (postcodes.io, free)
        try {
          const { data } = await api.get("/address-finder/lookup/reverse", {
            params: { lat: p.coords.latitude, lng: p.coords.longitude },
          });
          setForm((f) => ({
            ...f,
            postcode: f.postcode || data.postcode || "",
            town: f.town || data.town || "",
          }));
          if (data.postcode) toast.info(`Postcode: ${data.postcode}`);
        } catch { /* silent — outside UK or no coverage */ }
      },
      (e) => { toast.error(`GPS: ${e.message}`); setGpsBusy(false); },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  // Look up postcode → fill lat/lng + town (when driver types postcode manually)
  const lookupPostcode = async (pc) => {
    const p = (pc || "").trim();
    if (p.length < 5) return;
    try {
      const { data } = await api.get("/address-finder/lookup/postcode", { params: { postcode: p } });
      setForm((f) => ({
        ...f,
        latitude: data.lat ?? f.latitude ?? null,
        longitude: data.lng ?? f.longitude ?? null,
        town: f.town || data.town || "",
      }));
      toast.success(`Located ${data.postcode}`);
    } catch { /* silent */ }
  };

  useEffect(() => { getGps(); /* eslint-disable-next-line */ }, []);

  // Merge AI-extracted fields into the form ONLY for empty fields — never overwrite user input.
  const mergeAiFields = (fields) => {
    if (!fields || typeof fields !== "object") return [];
    const map = {
      title: "title",
      property_name: "property_name",
      address_line_1: "address_line_1",
      town: "town",
      postcode: "postcode",
      entrance_instructions: "entrance_instructions",
      vehicle_size_warning: "vehicle_size_warning",
      landmark: "landmark",
    };
    const filled = [];
    setForm((f) => {
      const next = { ...f };
      for (const [aiKey, formKey] of Object.entries(map)) {
        const val = fields[aiKey];
        if (!val) continue;
        if (next[formKey] && String(next[formKey]).trim() !== "") continue;
        next[formKey] = formKey === "postcode" ? String(val).toUpperCase() : String(val);
        filled.push(formKey);
      }
      // If AI saw visible_signs but no title yet, use it as title fallback
      if (!next.title && fields.visible_signs) {
        next.title = String(fields.visible_signs).slice(0, 60);
        filled.push("title");
      }
      return next;
    });
    return filled;
  };

  const analyseImage = async (file) => {
    setAiBusy(true);
    setAiSummary(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/address-finder/analyse-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 45000,
      });
      if (data?.ok && data?.fields) {
        const filled = mergeAiFields(data.fields);
        if (filled.length) {
          toast.success(`AI filled ${filled.length} field${filled.length > 1 ? "s" : ""}`);
          setAiSummary({ ok: true, filledKeys: filled });
        } else {
          toast.info("AI processed the image but nothing new to fill");
          setAiSummary({ ok: true, filledKeys: [] });
        }
      } else {
        toast.warning("AI couldn't read that image — please fill in manually");
        setAiSummary({ ok: false, reason: data?.reason || "No fields extracted" });
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "AI request failed";
      toast.error(`AI: ${detail}`);
      setAiSummary({ ok: false, reason: detail });
    }
    setAiBusy(false);
  };

  const onPickImage = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    // reset input so the same file can be re-picked
    e.target.value = "";
    const added = files.map((f) => ({ file: f, previewUrl: URL.createObjectURL(f), analysed: false }));
    setImages((prev) => [...prev, ...added]);
    // Auto-analyse the FIRST newly added image only (avoid burning credits on every extra photo)
    const noPriorAnalysis = images.every((im) => !im.analysed);
    if (noPriorAnalysis) {
      const first = added[0];
      await analyseImage(first.file);
      setImages((prev) => prev.map((im) => (im.file === first.file ? { ...im, analysed: true } : im)));
    }
  };

  const reanalyse = async (im) => {
    await analyseImage(im.file);
    setImages((prev) => prev.map((x) => (x.file === im.file ? { ...x, analysed: true } : x)));
  };

  const checkDupes = async () => {
    try {
      const { data } = await api.post("/address-finder/check-duplicates", {
        latitude: form.latitude, longitude: form.longitude,
        postcode: form.postcode, title: form.title,
      });
      setDupes(data.duplicates || []);
      return data.duplicates || [];
    } catch { return []; }
  };

  const save = async () => {
    if (!form.title || !form.postcode) { toast.error("Title & postcode required"); return; }
    setBusy(true);

    const uuid = `offline-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const payload = { ...form, offline_client_uuid: uuid };
    const filesToUpload = images.map((im) => im.file);

    // Offline path — queue in IndexedDB, sync when back online.
    if (!navigator.onLine) {
      try {
        await queueAddress(payload, filesToUpload);
        toast.success("Saved offline — will sync when back online");
        nav("/driver/address-finder");
      } catch (e) {
        toast.error("Failed to save offline");
      }
      setBusy(false);
      return;
    }

    try {
      const d = await checkDupes();
      if (d.length && !window.confirm(`${d.length} similar address(es) found. Save anyway?`)) {
        setBusy(false); return;
      }
      const { data } = await api.post("/address-finder", payload);
      for (const f of filesToUpload) {
        const fd = new FormData();
        fd.append("file", f);
        try {
          await api.post(`/address-finder/${data.id}/images?image_type=entrance`, fd,
            { headers: { "Content-Type": "multipart/form-data" } });
        } catch (e) { console.warn("image upload failed", e); }
      }
      toast.success("Address saved");
      nav(`/driver/address-finder/${data.id}`);
    } catch (e) {
      // Network error while claiming to be online → queue as fallback
      if (e?.code === "ERR_NETWORK" || !navigator.onLine) {
        try {
          await queueAddress(payload, filesToUpload);
          toast.success("Network failed — saved offline, will sync later");
          nav("/driver/address-finder");
        } catch { toast.error("Save failed"); }
      } else {
        toast.error(e?.response?.data?.detail || "Save failed");
      }
    }
    setBusy(false);
  };

  return (
    <div className="p-4 pt-6 space-y-4 pb-32">
      <Button variant="ghost" size="sm" onClick={() => nav(-1)} data-testid="af-back">
        <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
      </Button>

      <h1 className="font-heading text-2xl font-bold tracking-tight">Add address</h1>

      <Card className="p-4 rounded-2xl border-brand-border space-y-3">
        <div className="text-[10px] uppercase tracking-wider text-brand-red font-mono">1 · Location</div>
        <div className="flex items-center gap-2">
          <Button onClick={getGps} disabled={gpsBusy} className="flex-1" data-testid="af-gps-btn">
            {gpsBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MapPin className="w-4 h-4 mr-2" />}
            Use my location
          </Button>
        </div>
        {form.latitude && (
          <>
            <div className="text-xs font-mono text-slate-500">
              {form.latitude.toFixed(6)}, {form.longitude.toFixed(6)} · ±{form.gps_accuracy}m
            </div>
            <a href={`https://www.google.com/maps?q=${form.latitude},${form.longitude}`}
               target="_blank" rel="noreferrer" className="text-xs text-brand-navy underline">
              Preview on Google Maps
            </a>
          </>
        )}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">Latitude</Label>
            <Input type="number" step="any" value={form.latitude ?? ""}
                   onChange={(e) => set("latitude", e.target.value ? parseFloat(e.target.value) : null)}
                   data-testid="af-lat" />
          </div>
          <div>
            <Label className="text-xs">Longitude</Label>
            <Input type="number" step="any" value={form.longitude ?? ""}
                   onChange={(e) => set("longitude", e.target.value ? parseFloat(e.target.value) : null)}
                   data-testid="af-lng" />
          </div>
        </div>
      </Card>

      <Card className="p-4 rounded-2xl border-brand-border space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-wider text-brand-red font-mono">2 · Photos</div>
          <div className="flex items-center gap-1 text-[10px] text-slate-500">
            <Sparkles className="w-3 h-3 text-amber-500" /> AI auto-fill on
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          {/* Camera option */}
          <label className="flex flex-col items-center justify-center gap-1 p-3 border-2 border-dashed border-brand-border rounded-xl cursor-pointer hover:bg-slate-50">
            <Camera className="w-5 h-5 text-brand-red" />
            <span className="text-xs font-medium">Take photo</span>
            <input type="file" accept="image/*" capture="environment" multiple hidden
                   onChange={onPickImage} data-testid="af-photo-camera-input" />
          </label>
          {/* Gallery option (no capture attribute -> opens gallery / file picker) */}
          <label className="flex flex-col items-center justify-center gap-1 p-3 border-2 border-dashed border-brand-border rounded-xl cursor-pointer hover:bg-slate-50">
            <ImagePlus className="w-5 h-5 text-brand-navy" />
            <span className="text-xs font-medium">From gallery</span>
            <input type="file" accept="image/*" multiple hidden
                   onChange={onPickImage} data-testid="af-photo-gallery-input" />
          </label>
        </div>

        {aiBusy && (
          <div className="flex items-center gap-2 text-xs text-brand-navy bg-slate-50 border border-brand-border rounded-lg p-2"
               data-testid="af-ai-busy">
            <Loader2 className="w-3 h-3 animate-spin" />
            AI is reading your image…
          </div>
        )}
        {!aiBusy && aiSummary?.ok && aiSummary.filledKeys?.length > 0 && (
          <div className="flex items-start gap-2 text-xs bg-amber-50 border border-amber-200 rounded-lg p-2"
               data-testid="af-ai-summary">
            <Sparkles className="w-3 h-3 text-amber-600 mt-0.5" />
            <span>AI filled: <strong>{aiSummary.filledKeys.join(", ")}</strong>. Please review below.</span>
          </div>
        )}
        {!aiBusy && aiSummary && !aiSummary.ok && (
          <div className="text-xs text-slate-500" data-testid="af-ai-nofit">
            AI couldn&apos;t extract fields — fill in manually below.
          </div>
        )}

        {images.length > 0 && (
          <div className="grid grid-cols-3 gap-2">
            {images.map((im, i) => (
              <div key={i} className="relative aspect-square rounded-lg overflow-hidden bg-slate-100 group">
                <img src={im.previewUrl} alt="" className="w-full h-full object-cover" />
                <button onClick={() => setImages((p) => p.filter((_, j) => j !== i))}
                        className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-600 text-white grid place-items-center"
                        data-testid={`af-photo-remove-${i}`}>
                  <X className="w-3 h-3" />
                </button>
                <button onClick={() => reanalyse(im)} disabled={aiBusy}
                        className="absolute bottom-1 left-1 flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-black/60 text-white text-[10px] hover:bg-black/80 disabled:opacity-50"
                        data-testid={`af-photo-reanalyse-${i}`}>
                  <Sparkles className="w-2.5 h-2.5" /> AI
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="p-4 rounded-2xl border-brand-border space-y-3">
        <div className="text-[10px] uppercase tracking-wider text-brand-red font-mono">3 · Address (review AI or fill manually)</div>
        <div>
          <Label className="text-xs">Title *</Label>
          <Input value={form.title} onChange={(e) => set("title", e.target.value)}
                 placeholder="e.g. Blue gate farm, Warehouse rear entrance"
                 data-testid="af-title" />
        </div>
        <div>
          <Label className="text-xs">Postcode *</Label>
          <Input value={form.postcode}
                 onChange={(e) => set("postcode", e.target.value.toUpperCase())}
                 onBlur={(e) => lookupPostcode(e.target.value)}
                 placeholder="SY16 1AA" data-testid="af-postcode" />
        </div>
        <div>
          <Label className="text-xs">Property / business name</Label>
          <Input value={form.property_name} onChange={(e) => set("property_name", e.target.value)}
                 data-testid="af-property" />
        </div>
        <div>
          <Label className="text-xs">Address line 1</Label>
          <Input value={form.address_line_1} onChange={(e) => set("address_line_1", e.target.value)}
                 data-testid="af-line1" />
        </div>
        <div>
          <Label className="text-xs">Town / city</Label>
          <Input value={form.town} onChange={(e) => set("town", e.target.value)}
                 data-testid="af-town" />
        </div>
        <div>
          <Label className="text-xs">Entrance instructions</Label>
          <Textarea rows={2} value={form.entrance_instructions}
                    onChange={(e) => set("entrance_instructions", e.target.value)}
                    placeholder="Take second gate, buzzer #4, leave at the porch"
                    data-testid="af-entrance" />
        </div>
        <div>
          <Label className="text-xs">Landmark</Label>
          <Input value={form.landmark} onChange={(e) => set("landmark", e.target.value)}
                 placeholder="Next to red barn / opposite the pub" data-testid="af-landmark" />
        </div>
        <div>
          <Label className="text-xs">What3Words</Label>
          <Input value={form.what3words}
                 onChange={(e) => set("what3words", e.target.value.toLowerCase().replace(/\s+/g, ""))}
                 placeholder="lion.jumps.sparkle" data-testid="af-w3w" />
          {form.what3words && /^[a-z]+\.[a-z]+\.[a-z]+$/.test(form.what3words) && (
            <a href={`https://what3words.com/${form.what3words}`} target="_blank" rel="noreferrer"
               className="text-xs text-brand-navy underline mt-1 inline-block">
              Open ///{form.what3words} on What3Words
            </a>
          )}
        </div>
        <div>
          <Label className="text-xs flex items-center gap-1"><AlertTriangle className="w-3 h-3 text-amber-600" /> Vehicle warning</Label>
          <Input value={form.vehicle_size_warning}
                 onChange={(e) => set("vehicle_size_warning", e.target.value)}
                 placeholder="e.g. No large vans past bridge" data-testid="af-warning" />
        </div>
        <div>
          <Label className="text-xs">Driver notes</Label>
          <Textarea rows={2} value={form.driver_notes} onChange={(e) => set("driver_notes", e.target.value)}
                    data-testid="af-notes" />
        </div>
      </Card>

      <Button onClick={save} disabled={busy || !form.title || !form.postcode}
              className="w-full h-12 bg-brand-red hover:bg-brand-red-hover"
              data-testid="af-save-btn">
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
        Save address
      </Button>
    </div>
  );
};

export default AddressFinderAdd;
